import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from './cn';

interface PaginationProps {
	currentPage: number;
	totalPages: number;
	totalCount: number;
	hasNextPage: boolean;
	hasPreviousPage: boolean;
	onPageChange: (page: number) => void;
	limit: number;
}

export function Pagination({
	currentPage,
	totalPages,
	totalCount,
	hasNextPage,
	hasPreviousPage,
	onPageChange,
	limit,
}: PaginationProps) {
	if (totalPages <= 1) return null;

	const from = (currentPage - 1) * limit + 1;
	const to = Math.min(currentPage * limit, totalCount);

	// Generate page numbers to show
	const pages: (number | 'ellipsis')[] = [];
	if (totalPages <= 7) {
		for (let i = 1; i <= totalPages; i++) pages.push(i);
	} else {
		pages.push(1);
		if (currentPage > 3) pages.push('ellipsis');
		for (
			let i = Math.max(2, currentPage - 1);
			i <= Math.min(totalPages - 1, currentPage + 1);
			i++
		) {
			pages.push(i);
		}
		if (currentPage < totalPages - 2) pages.push('ellipsis');
		pages.push(totalPages);
	}

	return (
		<div className="flex items-center justify-between pt-4">
			<span className="text-sm text-text-4">
				{from}–{to} of {totalCount}
			</span>
			<div className="flex items-center gap-1">
				<button
					onClick={() => onPageChange(currentPage - 1)}
					disabled={!hasPreviousPage}
					className={cn(
						'flex h-7 w-7 items-center justify-center rounded-md text-text-3 transition-colors',
						hasPreviousPage
							? 'cursor-pointer hover:bg-surface-hover hover:text-text-1'
							: 'cursor-not-allowed opacity-40'
					)}
				>
					<ChevronLeft size={14} />
				</button>
				{pages.map((page, i) =>
					page === 'ellipsis' ? (
						<span
							key={`ellipsis-${i}`}
							className="flex h-7 w-7 items-center justify-center text-xs text-text-4"
						>
							…
						</span>
					) : (
						<button
							key={page}
							onClick={() => onPageChange(page)}
							className={cn(
								'flex h-7 min-w-7 cursor-pointer items-center justify-center rounded-md px-1.5 font-mono text-xs transition-colors',
								page === currentPage
									? 'bg-surface-fill text-text-1'
									: 'text-text-3 hover:bg-surface-hover hover:text-text-1'
							)}
						>
							{page}
						</button>
					)
				)}
				<button
					onClick={() => onPageChange(currentPage + 1)}
					disabled={!hasNextPage}
					className={cn(
						'flex h-7 w-7 items-center justify-center rounded-md text-text-3 transition-colors',
						hasNextPage
							? 'cursor-pointer hover:bg-surface-hover hover:text-text-1'
							: 'cursor-not-allowed opacity-40'
					)}
				>
					<ChevronRight size={14} />
				</button>
			</div>
		</div>
	);
}
